import {
  Shield,
  Gem,
  Layers,
  Wallet,
  Fuel,
  BadgeCheck,
  Store,
  Share2,
} from 'lucide-react';
import type { PathStep, PathStepLink } from '../components/PathSteps';
import { KYC_APERTUM_URL } from '../web3/didEligibility';

/** Live Classics collection: SportsFuture Olympic Series (SFUTOLYM) — used by NFTCard / NFTShowcase */
export const SFUT_NFT_CONTRACT = '0xa1d1E588412b998618C1bBc2029eAf5eFF30DE8b';
export const SFUT_TOKEN_CONTRACT = '0xA8532d029320AD0176EfD0959e044aA610A751Ed';

/** Shared APTMDAO entry (mining / APTM access) */
export const APTMDAO_REFERRAL = 'https://aptmdao.info/?referral=498';

export const CLASSICS_LINKS = {
  openPlaza: 'https://openplaza.io',
  openPlazaCollection: `https://openplaza.io/nft/${SFUT_NFT_CONTRACT}`,
  alchemyPay: 'https://alchemypay.org/',
  apertum: 'https://apertum.io/',
  apertumExplorerNft: `https://explorer.apertum.io/token/${SFUT_NFT_CONTRACT}`,
  apertumExplorerSfut: `https://explorer.apertum.io/token/${SFUT_TOKEN_CONTRACT}`,
  apertumDex: 'https://dex.apertum.io/',
  aptmDao: APTMDAO_REFERRAL,
  metaMask: 'https://metamask.io/download/',
  walletGuideVideo: 'https://www.youtube.com/watch?v=XKz5MS05zUk',
  kyc: KYC_APERTUM_URL,
  socialX: 'https://x.com/secretgiant1',
  socialTelegram: 'https://t.me/SPORTSFUTURE',
  socialYoutube: 'https://www.youtube.com/@SportsFutureio',
  socialInstagram: 'https://www.instagram.com/skyapertum/',
} as const;

export const CLASSICS_HEADER =
  'The new home for SportsFuture Legacy. Mint, own, and trade sports history the classic way — single unique NFTs and limited editions.';

export const CLASSICS_TRANSITION =
  'Classics is the starting point of the phased transition from the sports history into the future of your Legacy on the blockchain.';

export const CLASSICS_FEATURES = [
  {
    id: 'save-protect',
    title: 'Save & Protect',
    body: 'Securely mint and preserve iconic sports moments, memorabilia, and legacies on the blockchain with permanent provenance.',
    icon: Shield,
    ctaLabel: 'View Olympic Series contract',
    href: CLASSICS_LINKS.apertumExplorerNft,
  },
  {
    id: 'sell-unique',
    title: 'Sell Unique NFT',
    body: 'Mint one-of-one NFTs of your assets and sell them directly on the SportsFuture marketplace.',
    icon: Gem,
    ctaLabel: 'Mint Unique NFT on OpenPlaza',
    href: CLASSICS_LINKS.openPlaza,
  },
  {
    id: 'sponsorships-edition',
    title: 'Create Sponsorships NFT (Edition)',
    body: 'Mint limited editions tied to sponsorships, brands, or fan communities for broader reach and revenue.',
    icon: Layers,
    ctaLabel: 'Create Edition Series on OpenPlaza',
    href: CLASSICS_LINKS.openPlaza,
  },
] as const;

export type ClassicsLink = PathStepLink;
export type ClassicsStep = PathStep;

export const CLASSICS_STEPS: ClassicsStep[] = [
  {
    step: 1,
    title: 'Create a wallet',
    description:
      'Set up a self-custodial wallet so you control your keys. Use MetaMask (or another self-custody wallet) with Apertum, and follow our visual guide for APTM / DEX setup.',
    icon: Wallet,
    links: [
      { label: 'Download MetaMask (self-custodial)', href: CLASSICS_LINKS.metaMask },
      { label: 'Watch wallet & APTM guide (video)', href: CLASSICS_LINKS.walletGuideVideo },
      { label: 'Apertum network home', href: CLASSICS_LINKS.apertum },
    ],
  },
  {
    step: 2,
    title: 'Fulfill KYC',
    description:
      'Complete identity verification through Apertum KYC so you can participate fully in the ecosystem.',
    icon: BadgeCheck,
    links: [{ label: 'Fulfill KYC', href: CLASSICS_LINKS.kyc }],
  },
  {
    step: 3,
    title: 'Buy/Mine APTM for gasfee',
    description:
      'You need APTM for gas on Apertum. Buy APTM with fiat through AlchemyPay, mine or get APTM through APTMDAO, or trade on the Apertum DEX.',
    icon: Fuel,
    links: [
      { label: 'Buy APTM via AlchemyPay', href: CLASSICS_LINKS.alchemyPay },
      { label: 'APTMDAO / APTM Mining', href: CLASSICS_LINKS.aptmDao },
      { label: 'Trade on Apertum DEX', href: CLASSICS_LINKS.apertumDex },
    ],
  },
  {
    step: 4,
    title: 'Go To OpenPlaza and Mint NFT and or start NFT sale or Editions',
    description:
      'OpenPlaza connects to the live SportsFuture Olympic Series collection. Mint a unique one-of-one or create an edition series, then list for sale.',
    icon: Store,
    links: [
      { label: 'OpenPlaza — Mint & sell', href: CLASSICS_LINKS.openPlaza },
      { label: 'Olympic Series on OpenPlaza', href: CLASSICS_LINKS.openPlazaCollection },
      { label: 'Olympic Series contract (explorer)', href: CLASSICS_LINKS.apertumExplorerNft },
    ],
  },
  {
    step: 5,
    title: 'Share on your socials',
    description:
      'Spread your Classics mint or edition — share to X, Telegram, YouTube, or Instagram and grow your Legacy audience.',
    icon: Share2,
    links: [
      { label: 'Share on X', href: CLASSICS_LINKS.socialX },
      { label: 'Telegram community', href: CLASSICS_LINKS.socialTelegram },
      { label: 'YouTube', href: CLASSICS_LINKS.socialYoutube },
      { label: 'Instagram', href: CLASSICS_LINKS.socialInstagram },
    ],
    shareAction: true,
  },
];

/** Wallet → KYC → APTM. Shown on /apply before SportsFuture signup. */
export const KYC_ONBOARDING_STEPS: ClassicsStep[] = CLASSICS_STEPS.slice(0, 3);
